/**
 * Subtle always-visible time cue for the last 120s of a session (session-governance §6d).
 * A thin bar at the top of the screen — no text, just a visual indicator.
 *   - null or > 120s: invisible
 *   - <= 120s > 60s: brass bar depleting
 *   - <= 60s: amber bar depleting (heightened urgency)
 *
 * Reduced-motion: transition disabled (--dur-* tokens are set to 0ms globally).
 */

type Props = {
  secondsLeft: number | null;
};

const FULL_RANGE = 120;

export function TimeRemainingCue({ secondsLeft }: Props) {
  if (secondsLeft === null || secondsLeft > FULL_RANGE) return null;

  const pct = Math.max(0, Math.min(100, (secondsLeft / FULL_RANGE) * 100));
  const isUrgent = secondsLeft <= 60;

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '5px',
        backgroundColor: 'var(--c-fill)',
        zIndex: 'var(--z-overlay)' as unknown as number,
      }}
    >
      <div
        style={{
          height: '100%',
          width: `${pct}%`,
          background: isUrgent ? 'var(--c-warn)' : 'var(--foil-brass)',
          transition:
            'width var(--dur-3) var(--ease-physical), background var(--dur-2) var(--ease-out)',
        }}
      />
    </div>
  );
}
